<div class="messages-layout">
  <a routerLink="/dashboard" class="back-link">
    <i class="bi bi-arrow-left"></i> Back to Dashboard
  </a>

  <h1 class="page-title">Messages</h1>
  <p class="page-subtitle">Contact support and manage your message threads. View inbox and sent messages.</p>

  @if (error) {
    <div class="alert alert-danger">{{ error }}</div>
  }

  @if (!canSendNewMessage) {
    <div class="alert alert-warning">
      <i class="bi bi-exclamation-triangle me-2"></i>{{ dailyLimitMessage }}
    </div>
  }

  @if (loading) {
    <div class="panel messages-panel">
      <div class="panel-loading">
        <span class="spinner-border spinner-border-sm me-2" role="status"></span>
        Loading…
      </div>
    </div>
  } @else {
    <div class="messages-grid">
      <div class="panel messages-list-panel">
        <div class="panel-header">
          <div class="panel-header-left">
            <div class="inbox-toggle">
              <button type="button" class="inbox-btn" [class.active]="inboxView === 'inbox'" (click)="setInboxView('inbox')">Inbox</button>
              <button type="button" class="inbox-btn" [class.active]="inboxView === 'sent'" (click)="setInboxView('sent')">Sent</button>
            </div>
            <span class="results-badge">{{ totalElements }}</span>
          </div>
          <button type="button" class="btn btn-sm btn-primary" (click)="openNewMessage()" [disabled]="!canSendNewMessage">
            <i class="bi bi-plus-lg me-1"></i> New Message
          </button>
        </div>
        <div class="panel-body p-0">
          <div class="messages-table-wrap">
            <table class="messages-table">
              <thead>
                <tr>
                  <th (click)="setSort('topic')" class="sortable" role="button" tabindex="0">
                    Topic <i class="bi" [ngClass]="sortIcon('topic')"></i>
                  </th>
                  <th (click)="setSort('createdAt')" class="sortable" role="button" tabindex="0">
                    Date <i class="bi" [ngClass]="sortIcon('createdAt')"></i>
                  </th>
                </tr>
              </thead>
              <tbody>
                @for (msg of sortedMessages; track msg.id) {
                  <tr
                    class="message-row"
                    [class.selected]="isInThread(msg.id)"
                    [class.unread]="inboxView === 'inbox' && !msg.readAt"
                    (click)="selectMessage(msg)"
                    role="button"
                    tabindex="0">
                    <td>{{ msg.topic }}</td>
                    <td>{{ formatDate(msg.createdAt) }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          @if (sortedMessages.length === 0) {
            <p class="messages-empty">No messages.</p>
          }
          @if (totalPages > 1) {
            <nav class="messages-pagination">
              <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="page === 0" (click)="goToPage(page - 1)">
                «
              </button>
              <span class="pagination-info">Page {{ page + 1 }} of {{ totalPages }}</span>
              <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="page >= totalPages - 1" (click)="goToPage(page + 1)">
                »
              </button>
            </nav>
          }
        </div>
      </div>

      <div class="panel messages-detail-panel">
        @if (selectedLoading) {
          <div class="panel-loading">
            <span class="spinner-border spinner-border-sm me-2" role="status"></span>
            Loading…
          </div>
        } @else if (threadMessages.length > 0) {
          <div class="thread-header">
            <span class="thread-title">Thread ({{ threadMessages.length }} message{{ threadMessages.length !== 1 ? 's' : '' }})</span>
            <button type="button" class="btn btn-sm btn-primary" (click)="openReply()" [disabled]="!canSendNewMessage">
              <i class="bi bi-reply me-1"></i> Reply
            </button>
          </div>
          <div class="thread-body">
            @for (m of threadMessages; track m.id) {
              <div class="thread-message" [class.last]="$last">
                <dl class="thread-meta">
                  <dt>{{ m.recipientUserId ? 'To' : 'From' }}</dt>
                  <dd>{{ m.recipientUserId ? m.recipientUserName : m.userName }}</dd>
                  <dt>Date</dt>
                  <dd>{{ formatDate(m.createdAt) }}</dd>
                </dl>
                <div class="message-html message-content" [innerHTML]="m.messageText | safeHtml"></div>
                @if (m.hasAttachment) {
                  <p class="attachment-link mt-2 mb-0">
                    <button type="button" class="btn btn-link btn-sm p-0 text-decoration-none" (click)="downloadAttachment(m)">
                      <i class="bi bi-download me-1"></i>{{ m.attachmentFilename || 'attachment' }}
                    </button>
                  </p>
                }
              </div>
            }
          </div>
        } @else {
          <div class="messages-placeholder">
            <p class="text-muted mb-0">Select a message to view the conversation.</p>
          </div>
        }
      </div>
    </div>
  }
</div>

<!-- Compose modal (New Message / Reply) -->
@if (composeModal) {
  <div class="modal-overlay compose-overlay">
    <div class="modal-dialog compose-modal-dialog" (click)="$event.stopPropagation()"
      [style.top.px]="composeModalTop"
      [style.left.px]="composeModalLeft"
      (mousedown)="$event.stopPropagation()">
      <div class="modal-content">
        <div class="modal-header compose-drag-handle cursor-move" (mousedown)="onComposeDragStart($event)">
          <h5 class="modal-title">{{ composeMode === 'reply' ? 'Reply' : 'New Message' }}</h5>
          <button type="button" class="btn-close" (click)="requestCloseCompose()"></button>
        </div>
        <div class="modal-body">
          @if (composeMode === 'new') {
            <div class="mb-3">
              <label for="newMessageTopic" class="form-label fw-semibold">Topic</label>
              <select id="newMessageTopic" class="form-select" [(ngModel)]="newMessageTopic">
                <option value="">Select a topic…</option>
                @for (t of topics; track t.value) {
                  <option [value]="t.value">{{ t.label }}</option>
                }
              </select>
            </div>
          }
          <div class="mb-3">
            <label class="form-label fw-semibold">Message</label>
            @if (newMessageEditor) {
              <ngx-editor-menu [editor]="newMessageEditor"></ngx-editor-menu>
              <ngx-editor [editor]="newMessageEditor" [(ngModel)]="newMessageHtml" outputFormat="html" placeholder="Type your message…"></ngx-editor>
            }
          </div>
          @if (composeMode === 'reply') {
            <div class="mb-3">
              <label class="form-label fw-semibold">Attachment</label>
              <input type="file" class="form-control" (change)="onAttachmentChange($event)" />
              @if (newMessageAttachment) {
                <p class="mb-0 mt-1 small"><i class="bi bi-paperclip"></i> {{ newMessageAttachment.name }}</p>
              }
            </div>
          }
          @if (!canSendNewMessage) {
            <div class="alert alert-warning mb-0">{{ dailyLimitMessage }}</div>
          }
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="requestCloseCompose()">Cancel</button>
          <button type="button" class="btn btn-primary" (click)="showComposeSendConfirm()"
            [disabled]="!canSendCompose || newMessageSending">
            @if (newMessageSending) { Sending… } @else { {{ composeMode === 'reply' ? 'Send reply' : 'Send' }} }
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Compose cancel confirm -->
@if (composeCancelConfirm) {
  <div class="modal-overlay modal-overlay-high" (click)="cancelComposeConfirmClose()">
    <div class="modal-dialog modal-dialog-centered" (click)="$event.stopPropagation()">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Discard message?</h5>
          <button type="button" class="btn-close" (click)="cancelComposeConfirmClose()"></button>
        </div>
        <div class="modal-body">
          <p class="mb-0">Your message has not been sent. Discard it?</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="cancelComposeConfirmClose()">Keep editing</button>
          <button type="button" class="btn btn-outline-danger" (click)="confirmCloseCompose()">Discard</button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Compose send confirm -->
@if (composeSendConfirm) {
  <div class="modal-overlay modal-overlay-high" (click)="cancelComposeSend()">
    <div class="modal-dialog modal-dialog-centered" (click)="$event.stopPropagation()">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ composeMode === 'reply' ? 'Send reply' : 'Send message' }}</h5>
          <button type="button" class="btn-close" (click)="cancelComposeSend()"></button>
        </div>
        <div class="modal-body">
          @if (newMessageSendError) {
            <div class="alert alert-danger mb-3">{{ newMessageSendError }}</div>
          }
          <p>{{ composeMode === 'reply' ? 'Send this reply?' : 'Send this message to Support?' }}</p>
          <div class="message-html message-preview" [innerHTML]="newMessageHtml | safeHtml"></div>
          @if (newMessageAttachment) {
            <p class="mt-2 mb-0 small"><i class="bi bi-paperclip"></i> {{ newMessageAttachment.name }}</p>
          }
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="cancelComposeSend()">Cancel</button>
          <button type="button" class="btn btn-primary" (click)="confirmSendCompose()" [disabled]="newMessageSending">
            @if (newMessageSending) { Sending… } @else { Send }
          </button>
        </div>
      </div>
    </div>
  </div>
}
